import type { ReactElement } from 'react';
import styled from '@emotion/styled';
import {
  Dropdown,
  DropdownMenu,
  DropdownSelectTrigger,
  Icon,
  Stack,
  Text,
  tokens,
  useTippy,
} from '@hotjar/hotsauce';
import type { Allowance } from '@hotjar/product-catalog';
import { useTranslation } from '@hotjar/translations';
import { convertReadableNumber } from '~/subscription-management/utils/allowance.utils';
import { usePrettifyPrice } from '~/subscription-management/utils/cost/prettify-price';
import type { Currency } from '~/subscription-management/api/common/model/customer';

const S = {
  DropdownMenu: styled(Dropdown)`
    max-height: 440px;
    list-style: none;

    /**
    * This hack is needed because we have a global rule for .tippy-content in insights.scss,
    * which applies extra padding, and it looks terrible with this dropdown, so we overwrite it.
    */
    .tippy-content {
      padding: 0;
    }

    &.tippy-box {
      background-color: ${tokens.color.ui_background};
    }
  `,
  Label: styled.div`
    ${tokens.truncateText()};
    flex-grow: 1;
    text-align: left;
  `,
  DropdownBaseItem: styled(DropdownMenu.BaseItem)`
    display: block;
    height: ${tokens.spacer.x6pt5};
    padding: ${tokens.spacer.x1} ${tokens.spacer.x1pt5};
  `,
  DropdownMessage: styled(DropdownMenu.BaseItem)`
    text-align: left;
    padding: 0;
  `,
  DropdownMessageItem: styled(Text)`
    background-color: ${tokens.color.bg_info};
    padding: ${tokens.spacer.x2};
  `,
  ItemContainer: styled(Stack)`
    align-items: center;
    display: flex;
    justify-content: flex-start;
    width: 100%;
  `,
  ProductPrice: styled(Text)`
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: ${tokens.spacer.x0pt5};
  `,
};

interface Props {
  allowances: Allowance[];
  currency: Currency;
  billingPeriod: 'MONTHLY' | 'ANNUALLY';
  selectedAllowance: Allowance;
  message?: ReactElement;
  onChange: (allowance: Allowance) => void;
}

export function AllowancesDropdown({
  allowances,
  selectedAllowance,
  currency,
  message,
  billingPeriod,
  onChange,
}: Props): JSX.Element {
  const { t: tProductInformation } = useTranslation('checkout', {
    keyPrefix: 'product-information',
  });
  const { t: tCommon } = useTranslation('checkout', { keyPrefix: 'wizard.common' });
  const { prettifyPrice } = usePrettifyPrice(currency, 0);
  const refInstance = useTippy();

  const getAllowanceTranslation = (allowance: Allowance): string => {
    if ('monthlyResponses' in allowance) {
      return tProductInformation(
        allowance.selfServeEnabled
          ? 'allowances.monthly-responses'
          : 'allowances.monthly-responses-from',
        {
          allowances: convertReadableNumber(allowance.monthlyResponses),
        },
      );
    }

    return tProductInformation(
      allowance.selfServeEnabled ? 'allowances.daily-sessions' : 'allowances.daily-sessions-from',
      {
        allowances: convertReadableNumber(allowance.dailyCcSessions),
      },
    );
  };

  const firstNonSelfServeItem = allowances.find((allowance) => !allowance.selfServeEnabled);

  return (
    <S.DropdownMenu
      appendTo="parent"
      onCreate={refInstance.set}
      trigger={
        <DropdownSelectTrigger
          selectedOption={{
            value: selectedAllowance.sku,
            label: getAllowanceTranslation(selectedAllowance),
          }}
        />
      }
      onShow={({ popper, reference }) => {
        // We overidde the width to match the length of the trigger element
        // eslint-disable-next-line no-param-reassign
        (popper.firstChild as HTMLElement).style.width = `${
          reference.getBoundingClientRect().width
        }px`;
      }}
    >
      {allowances
        /**
         * we display all self serve options and then bundle all non self serve into a single line item
         * so if firstNonSelfServeItem exists it means the list has at least one non self serve item and should be included
         */
        .filter(
          (allowance) => allowance.selfServeEnabled || firstNonSelfServeItem?.sku === allowance.sku,
        )
        .map((allowance) => {
          const allowanceTranslation = getAllowanceTranslation(allowance);
          const isSelected = selectedAllowance.sku === allowance.sku;
          return (
            <S.DropdownBaseItem
              key={allowance.sku}
              onClick={() => {
                onChange(allowance);
                refInstance.close();
              }}
              isSelected={isSelected}
            >
              <S.ItemContainer spacing="x1">
                <S.Label>{allowanceTranslation}</S.Label>
                {isSelected && <Icon icon="selected" />}
              </S.ItemContainer>
              <S.ProductPrice textType="caption" textColor="text_light" as="span">
                {allowance.selfServeEnabled && (
                  <>
                    {prettifyPrice(allowance.price)}{' '}
                    {tCommon(`billing-period-${billingPeriod.toLowerCase()}`)}
                  </>
                )}
                {!allowance.selfServeEnabled && (
                  <>{tProductInformation('allowances.yearly-subscription')}</>
                )}
              </S.ProductPrice>
            </S.DropdownBaseItem>
          );
        })}
      {message && (
        <S.DropdownMessage hasDivider>
          <S.DropdownMessageItem textType="caption" textColor="text_light">
            {message}
          </S.DropdownMessageItem>
        </S.DropdownMessage>
      )}
    </S.DropdownMenu>
  );
}
